import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mic, Sparkles, Phone, Loader2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const dur = (ms) => (ms ? `${Math.floor(ms / 60000)}:${String(Math.round((ms % 60000) / 1000)).padStart(2, "0")}` : "—");
const STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", denied: "bg-red-100 text-red-800" };

export const RefundDialog = ({ call, onClose, onDone, url }) => {
  const [reason, setReason] = useState("");
  const send = async () => {
    try { await api.post(url || `/live-transfers/calls/${call.call_id}/refund-request`, { reason }); toast.success("Refund request sent to your administrator"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="transfer-refund-dialog">
        <DialogHeader><DialogTitle>Request a refund ({money(call.charge)})</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Your administrator reviews each request. Approved refunds go straight back into your Live Transfers balance.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should this call be refunded? (e.g. wrong number, not a mortgage inquiry)" data-testid="transfer-refund-reason" />
        <DialogFooter><Button onClick={send} disabled={reason.trim().length < 5} data-testid="transfer-refund-submit">Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Actions = ({ c, admin, onRequest, reload }) => {
  const decide = async (approve) => {
    try { await api.post(`/admin/live-transfers/refunds/${c.call_id}`, { approve }); toast.success(approve ? "Refunded - credits are back in the MLO's balance" : "Refund denied"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refundNow = async () => {
    if (!window.confirm(`Refund ${money(c.charge)} to ${c.mlo_name || "the MLO"}'s Live Transfers balance now?`)) return;
    try { await api.post(`/admin/live-transfers/calls/${c.call_id}/refund`); toast.success("Refunded - credits are back in the MLO's balance"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (admin && c.refund_status === "pending") return (
    <div className="flex gap-1">
      <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => decide(true)} data-testid={`refund-approve-${c.call_id}`}>Approve &amp; refund</Button>
      <Button size="sm" variant="outline" className="text-red-700" onClick={() => decide(false)} data-testid={`refund-deny-${c.call_id}`}>Deny</Button>
    </div>
  );
  if (admin && c.can_refund) return <Button size="sm" variant="outline" className="text-emerald-700" onClick={refundNow} data-testid={`refund-now-${c.call_id}`}>Refund now</Button>;
  if (!admin && c.can_request) return <Button size="sm" variant="outline" onClick={() => onRequest(c)} data-testid={`refund-request-${c.call_id}`}>{c.refund_status === "denied" ? "Request again" : "Request refund"}</Button>;
  if (!admin && !c.refund_status) return <div><Button size="sm" variant="outline" disabled data-testid={`refund-request-${c.call_id}`}>Request refund</Button><div className="mt-1 text-xs text-slate-400">No charge on this call</div></div>;
  return null;
};

const NOTE_FIELDS = [["caller_name", "Name"], ["email", "Email"], ["property_address", "Property"], ["loan_type", "Loan type"],
  ["loan_amount", "Loan amount"], ["property_value", "Home value"], ["mortgage_balance", "Mortgage balance"], ["current_rate", "Current rate"],
  ["credit_score", "Credit"], ["income_employment", "Income / job"], ["timeline", "Timeline"], ["appointment", "Appointment"], ["next_steps", "Next steps"]];
const fmtPhone = (p) => (p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p);

export const CallNotes = ({ c, reload }) => {
  const n = c.notes;
  const regen = async () => {
    try { await api.post(`/live-transfers/calls/${c.call_id}/notes`); toast.success("Writing call notes - check back in a minute"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!n) return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid={`call-notes-pending-${c.call_id}`}>
      {c.notes_status === "processing" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />AI is writing the call notes…</>
        : c.notes_status === "failed" ? "AI couldn't write notes for this call." : c.recording_url ? "AI call notes not written yet." : "AI call notes will appear once the recording is ready."}
      {c.recording_url && c.notes_status !== "processing" && <Button size="sm" variant="ghost" className="h-7" onClick={regen} data-testid={`call-notes-make-${c.call_id}`}><Sparkles className="mr-1 h-3.5 w-3.5" />Write notes now</Button>}
    </div>
  );
  const name = n.caller_name || c.caller;
  const address = [n.property_address, n.city, [n.state, n.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const vals = { ...n, caller_name: name, property_address: address };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid={`call-lead-card-${c.call_id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900"><Sparkles className="h-4 w-4 text-amber-600" />AI call notes</div>
        {n.phone && <a href={`tel:${n.phone}`} className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white" data-testid={`call-lead-phone-${c.call_id}`}><Phone className="h-3.5 w-3.5" />{fmtPhone(n.phone)}</a>}
      </div>
      {n.summary && <p className="mb-3 text-sm text-slate-700" data-testid={`call-lead-summary-${c.call_id}`}>{n.summary}</p>}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {NOTE_FIELDS.filter(([k]) => vals[k]).map(([k, label]) => (
          <div key={k} className="flex flex-col sm:flex-row sm:gap-2" data-testid={`call-lead-${k}-${c.call_id}`}><dt className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt><dd className="text-slate-900">{vals[k]}</dd></div>
        ))}
      </dl>
      {!!n.key_notes?.length && <ul className="mt-3 list-disc space-y-0.5 pl-5 text-sm text-slate-700">{n.key_notes.map((x, i) => <li key={i}>{x}</li>)}</ul>}
      <Button size="sm" variant="ghost" className="mt-2 h-7 text-xs text-slate-500" onClick={regen} data-testid={`call-notes-redo-${c.call_id}`}>Rewrite notes</Button>
    </div>
  );
};

const Row = ({ c, admin, onRequest, reload }) => (
  <div data-testid={`transfer-call-${c.call_id}`} className="border-t border-slate-100 py-4 text-sm">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto_auto_auto] md:items-start">
    <div><div className="font-semibold text-slate-900">{c.caller}</div><div className="text-xs text-slate-500">{c.phone} · {new Date(c.created_at).toLocaleString()}</div>
      {admin && <div className="text-xs text-slate-500">MLO: {c.mlo_name || "—"}</div>}
      {c.summary && <div className="mt-1 max-w-md text-xs text-slate-600">{c.summary}</div>}</div>
    <div><Badge variant="outline">{c.live_transfer ? "Live transfer" : "Inbound"}</Badge><div className="mt-1 text-xs capitalize text-slate-500">{(c.outcome || "in progress").replace(/_/g, " ")} · {dur(c.duration_ms)}</div></div>
    <div>{c.recording_url ? <audio controls preload="none" src={c.recording_url} className="h-9 w-full md:w-56" data-testid={`transfer-recording-${c.call_id}`} /> : <span className="text-xs text-slate-400">No recording yet</span>}</div>
    <div className="font-semibold" data-testid={`transfer-charge-${c.call_id}`}>{c.charge ? money(c.charge) : "No charge"}</div>
    <div>
      {c.refund_status && <Badge className={`mb-1 border-0 capitalize ${STATUS[c.refund_status]}`} data-testid={`refund-status-${c.call_id}`}>Refund {c.refund_status}</Badge>}
      {admin && c.refund_reason && <div className="mb-1 max-w-xs text-xs text-slate-600">“{c.refund_reason}”</div>}
      <Actions c={c} admin={admin} onRequest={onRequest} reload={reload} />
    </div>
    </div>
    <div className="mt-3"><CallNotes c={c} reload={reload} /></div>
  </div>
);

export const TransferCallLog = ({ admin = false }) => {
  const [calls, setCalls] = useState(null);
  const [req, setReq] = useState(null);
  const load = () => api.get("/live-transfers/calls").then((r) => setCalls(r.data.calls)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <Card className="mb-5 p-6" data-testid="transfer-call-log">
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Mic className="h-5 w-5" />Inbound & live transfer recordings</h3>
      <p className="mb-4 text-sm text-slate-600">{admin ? "Every inbound and live transfer call. Refund now or Approve & refund puts the charge straight back in the MLO's Live Transfers balance (nothing goes back to their card)." : "Listen to each call. If a charged transfer wasn't a real mortgage inquiry, request a refund. Approved refunds go back into your Live Transfers balance."}</p>
      {calls && !calls.length && <p className="text-sm text-slate-500" data-testid="transfer-call-empty">No inbound calls yet.</p>}
      {!!calls?.length && <div>{calls.map((c) => <Row key={c.call_id} c={c} admin={admin} onRequest={setReq} reload={load} />)}</div>}
      {req && <RefundDialog call={req} onClose={() => setReq(null)} onDone={() => { setReq(null); load(); }} />}
    </Card>
  );
};
